import React, { useState, useEffect } from 'react';
import type { ExpertPageProfile } from '../../types';
import FavoriteToggle from '../client/FavoriteToggle';
import { clientPortalApi } from '../../lib/http';

interface ExpertHeaderProps {
  profile: ExpertPageProfile;
}

export const ExpertHeader: React.FC<ExpertHeaderProps> = ({ profile }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if this expert is in favorites
    const checkFavoriteStatus = async () => {
      try {
        setIsLoading(true);
        const response = await clientPortalApi.favorites.list();
        const favoriteExperts = response.data;
        const isFav = favoriteExperts.some(p => p.slug === profile.slug);
        setIsFavorite(isFav);
      } catch (error) {
        // User might not be logged in, that's okay
        console.debug('Could not check favorite status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkFavoriteStatus();
  }, [profile.slug]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {profile.avatar_url && (
            <img
              src={profile.avatar_url}
              alt={profile.public_title ?? profile.display_name}
              className="w-20 h-20 rounded-full object-cover"
            />
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {profile.public_title ?? (profile.business_name || profile.display_name)}
            </h1>
            {/* Secondary line: person's name when title is the brand. */}
            {profile.business_name && profile.business_name !== profile.display_name && (
              <p className="text-gray-500 text-sm mt-0.5">{profile.display_name}</p>
            )}
            {/* About text — prefer the new `description` field, fall back
                to the legacy `bio` for older server builds. */}
            {(profile.description?.trim() || profile.bio) && (
              <p className="text-gray-600 mt-2 whitespace-pre-line">
                {profile.description?.trim() || profile.bio}
              </p>
            )}
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              {profile.location.city && profile.location.country && (
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {profile.location.city}, {profile.location.country}
                </span>
              )}
              {profile.rating > 0 && (
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {profile.rating.toFixed(1)}
                </span>
              )}
              {profile.services_count > 0 && (
                <span>{profile.services_count} services</span>
              )}
            </div>
          </div>
        </div>
        <FavoriteToggle
          expertSlug={profile.slug}
          isFavorite={isFavorite}
          onToggle={(newState) => setIsFavorite(newState)}
        />
      </div>
    </div>
  );
};
